/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { Suspense, useState, useCallback } from 'react';
import Styles from './Stimpage.module.css';
import { StimulationSchedular } from '../../components/stimSchedular';
import HeaderProfile from '../../components/header_Profile/header_Profile.index';
import { usePatientContext } from '../../components/SubPatinetProvider';
import { PatientData } from '../../types';
import CycleSelector from './cycleSelector.stim';
import {ReactComponent as CycleSVG} from '../../assets/SVG/In vitro fertilization-bro 1.svg';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import Hormonalgraph from './hormonalgraph';
import GraphHeader from './graphHeader';
import FollicularGraph from './follicularGraph';
import { getStoredPermissions } from '../../utils/commonUtils';
import { StimulationTabPermission } from './helper.stim';
import { getButtonStates } from '../../utils/permissionService';
import { useTranslation } from 'react-i18next';
import NoAccessPage from '../noAccessPage';

const StimPage: React.FC = () => {

  interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
  }
  
  function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
  
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
      </div>
    );
  }
  const { t } = useTranslation('stimulation_config');

  const [selectedPatient, setselectedPatient] = useState<PatientData | null>(null);
  const [selectedStimChart, setSelectedStimChart] = useState<number | null>(null);
  const [graphType, setGraphType] = useState<'follicular' | 'hormonal'>('follicular');
  const [selectedSide, setSelectedSide] = useState<'left' | 'right'>('right');
  
  const {patientId} = usePatientContext();

  const [value, setValue] = React.useState(0);
  const { canView: canViewGraph } = getButtonStates('Clinical', 'Stimulation', 'Follicular Graph', 'female');
  const { canView: canViewChart } = getButtonStates('Clinical', 'Stimulation', 'Chart', 'female');
  const [isAllMales, setIsAllMales] = useState(false);
  
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleDatafromHeader = useCallback((data: PatientData) => {
    setselectedPatient(data);
  }, []);

  const handleAllMales = useCallback((allMales: boolean) => {
    setIsAllMales(allMales);
  }, []);

  const handleChangeCycle = useCallback((event: React.SyntheticEvent | null, value: number | null) => {
    setSelectedStimChart(value);
  }, []);

  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }


  const allowedStimulationPermissions = React.useMemo(() => {
    const permissionsData = getStoredPermissions();
    const clinicalModule = permissionsData?.modules?.find(
      (m: any) => m.name === 'Clinical' && m.featureEnabled
    );
    if (!clinicalModule) return { type: [] };
    const StimulationModule = clinicalModule.submodules?.find(
      (sm: any) => sm.name === 'Stimulation'
    );
    return StimulationModule || { type: [] };
  }, []);


  const follicularTabObj: StimulationTabPermission | undefined = Array.isArray(allowedStimulationPermissions.type)
    ? allowedStimulationPermissions.type.find((t: StimulationTabPermission) => t.name === 'Follicular Graph')
    : undefined;
  const chartTabObj: StimulationTabPermission | undefined = Array.isArray(allowedStimulationPermissions.type)
    ? allowedStimulationPermissions.type.find((t: StimulationTabPermission) => t.name === 'Chart')
    : undefined;

    
  const showFollicularTab =
    follicularTabObj &&
    ((selectedPatient?.gender === 'FEMALE' && follicularTabObj.female) ||
      (selectedPatient?.gender === 'MALE' && follicularTabObj.male));

  const showChartTab =
    chartTabObj &&
    ((selectedPatient?.gender === 'FEMALE' && chartTabObj.female) ||
      (selectedPatient?.gender === 'MALE' && chartTabObj.male));

  
  return (
    <div className={Styles.stimpage}>
      <div className={Styles.header}>
        <HeaderProfile onData={handleDatafromHeader} onAllMales={handleAllMales}/>
      </div>
      {isAllMales ? (
        <NoAccessPage  message={t('allMalesAccessRestricted')}/>
      ) : (
        <>
          <div className={Styles.cycleSelector}>
            {selectedPatient && <CycleSelector patient={patientId} onCycleChange={handleChangeCycle} IsFromStimulationReport={true} />}
          </div>
          <div>
            <div>
              <Tabs 
                value={value} 
                variant='fullWidth' 
                onChange={handleChange} 
                aria-label="basic tabs example" 
                TabIndicatorProps={{ sx: { bgcolor:'vidaiOrange'}}}
              >
                {showChartTab && <Tab label={t('Chart')} {...a11yProps(0)} />}
                {showFollicularTab && <Tab label={t('Follicular Graph')} {...a11yProps(1)} />}
              </Tabs>
            </div>
            <div>
              {showChartTab && (
                <CustomTabPanel value={value} index={0}>
                  <Suspense fallback={<Typography>{t('LoadingChart')}</Typography>}>
                    <div className={Styles.scheduler}>
                      {selectedStimChart ? <StimulationSchedular id={selectedStimChart} height={'75vh'}/>: <div className={Styles.logo}><CycleSVG/><Typography variant='h6'>{t('NoActiveStimCycleFound')}</Typography></div>}
                    </div>
                  </Suspense>
                </CustomTabPanel>
              )}
              {showFollicularTab && (
                <CustomTabPanel value={value} index={1}>
                  <Suspense fallback={<Typography>{t('LoadingGraph')}</Typography>}>
                    <GraphHeader
                      graphType={graphType}
                      follicularTabObj={follicularTabObj}
                      setGraphType={setGraphType}
                      selectedSide={selectedSide}
                      setSelectedSide={setSelectedSide}
                    />
                    <Box className={Styles.scheduler}>
                      {graphType === 'hormonal' ? <Hormonalgraph patientId={selectedPatient?.id ?? patientId} stim={Number(selectedStimChart)}/>: <FollicularGraph patientId={selectedPatient?.id ?? patientId} stim={Number(selectedStimChart)}/>}
                    </Box>
                  </Suspense>
                </CustomTabPanel>
              )}
              {(!canViewChart && !canViewGraph) && (
                <NoAccessPage />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default StimPage;